// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import type { ITokenRetriever } from '../types/external';
import type { IIframeApi, OnPreSessionErrorCallback } from '../types/internal';
import { HostWrapper } from './crossWindowChannel/hostWrapper';
import { navigateIframeToEmbedAsync } from './navigateIframeToEmbed';
import { ObservableProperty } from './observable';
import { OnePlayerError } from './onePlayerError';

export class OnePlayerIframe {
  private _iframe: HTMLIFrameElement | undefined;
  private _embedUrl: string;
  private readonly _containerDiv: HTMLDivElement;
  private readonly _tokenRetriever: ITokenRetriever;
  private readonly _disposeActions: (() => void)[];
  private readonly _onError: OnPreSessionErrorCallback;
  private readonly _navigateIframeToEmbedAsync: (
    iframe: HTMLIFrameElement,
    embedSrc: string,
    tokenRetriever: ITokenRetriever
  ) => Promise<void>;
  private _crossWindowChannel: HostWrapper | undefined;
  private readonly observer: MutationObserver | undefined = undefined;
  private readonly isIframeConnected = new ObservableProperty<boolean>(false);
  private readonly defaultIframeTitle: string = 'Microsoft Stream video and audio player';

  // We expect to navigate to 'about:blank' exactly one time - when the iframe is first added to the DOM.
  // If we ever navigate there again, it means that the iframe has been forcibly unloaded.
  private _hasNavigatedToAboutBlank = false;

  public constructor(
    embedUrl: string,
    containerDiv: HTMLDivElement,
    tokenRetriever: ITokenRetriever,
    onError: OnPreSessionErrorCallback,
    allowFullScreen?: boolean,
    navigateIframeToEmbedAsyncCallback: (
      iframe: HTMLIFrameElement,
      embedSrc: string,
      tokenRetriever: ITokenRetriever
    ) => Promise<void> = navigateIframeToEmbedAsync
  ) {
    this._iframe = document.createElement('iframe');
    this._iframe.style.flex = '1';
    this._iframe.style.border = 'none';
    if (allowFullScreen) this._iframe.allowFullscreen = true;
    this._iframe.allow = 'autoplay *;clipboard-write *;';
    this._iframe.setAttribute(
      'sandbox',
      'allow-forms allow-scripts allow-same-origin allow-popups allow-downloads'
    );
    this._iframe.setAttribute('title', this.defaultIframeTitle);
    const onLoad = this.onLoad.bind(this);
    this._iframe.addEventListener('load', onLoad);

    this._embedUrl = embedUrl;
    this._containerDiv = containerDiv;
    this._tokenRetriever = tokenRetriever;
    this._onError = onError;
    this._navigateIframeToEmbedAsync = navigateIframeToEmbedAsyncCallback;

    this._containerDiv.style.display = 'flex';
    this._containerDiv.appendChild(this._iframe);

    this.observer = new MutationObserver(() => {
      this.isIframeConnected.value = this._iframe?.isConnected || false;

      // Stop observing once the element has been connected
      if (this._iframe?.isConnected) {
        this.observer?.disconnect();
      }
    });

    this._disposeActions = [
      () => {
        if (this._iframe) {
          this._containerDiv.removeChild(this._iframe);
          this._iframe.removeEventListener('load', onLoad);
          this._iframe = undefined;
        }
        this.observer?.disconnect();
      }
    ];
  }

  /**
   * Navigates the iframe to the embed endpoint, connects the cross window channel, and returns the
   * IIFrameApi. When an existing IIFrameApi is passed in, it will be used to reconnect the cross window channel.
   *
   * @returns The IIFrameApi used to communicate with OnePlayer in the IFrame. In case an existing IIFrameApi
   *   was passed, the same one will be returned.
   */
  public async loadAndConnectToOnePlayer(existingIframeApi?: IIframeApi): Promise<IIframeApi> {
    if (this._iframe === undefined) {
      throw new Error('Cannot load and connect to OnePlayer because the iframe has been disposed.');
    }

    if (existingIframeApi) {
      // If an existing IFrameApi is passed in, we need to re-create the iframe element since the existing one will be cross domain.
      this.reCreateIframeElement();
    }
    await this.waitForIframeConnected();
    // Initialize the Host to Client communication via Channel Messaging API
    this._crossWindowChannel = new HostWrapper(
      this._embedUrl,
      this._iframe,
      this._tokenRetriever,
      this._onError,
      (value: string | undefined) => this._onTitleOrDisplayNameUpdate(value)
    );
    if (existingIframeApi) {
      this._crossWindowChannel.reconnectHost(existingIframeApi);
    } else {
      this._crossWindowChannel.createIframeApi();
    }
    this._disposeActions.push(() => this._crossWindowChannel?.dispose());
    this._embedUrl = this._crossWindowChannel.getDecoratedEmbedUrl();

    // Navigate to embed in the iframe
    try {
      await this._navigateIframeToEmbedAsync(this._iframe, this._embedUrl, this._tokenRetriever);
    } catch (error: unknown) {
      if (error instanceof OnePlayerError) {
        throw error;
      }
      throw OnePlayerError.createGenericError('fatal', 'loadIframe', 'navToEmbed', error);
    }

    return this._crossWindowChannel.iFrameApi;
  }

  public dispose(): void {
    for (const disposeAction of this._disposeActions) {
      disposeAction();
    }
    this._disposeActions.length = 0;
  }

  public reCreateIframeElement(): void {
    if (this._iframe) {
      this.dispose();
      this._hasNavigatedToAboutBlank = false;
    }
    this._iframe = document.createElement('iframe');
    this._iframe.style.flex = '1';
    this._iframe.style.border = 'none';
    this._iframe.allow = 'autoplay *;clipboard-write *;';
    this._iframe.setAttribute('sandbox', 'allow-forms allow-scripts allow-same-origin allow-popups');
    this._iframe.setAttribute('title', this.defaultIframeTitle);
    const onLoad = this.onLoad.bind(this);
    this._iframe.addEventListener('load', onLoad);

    this._containerDiv.style.display = 'flex';
    this._containerDiv.appendChild(this._iframe);
  }

  private async waitForIframeConnected(): Promise<void> {
    return new Promise((resolve) => {
      if (this._iframe?.isConnected) {
        resolve();
      } else {
        const unsubscribe = this.isIframeConnected.subscribe((isConnected) => {
          if (isConnected) {
            resolve();
          }
        });
        this._disposeActions.push(() => unsubscribe());
      }
    });
  }

  private onLoad(): void {
    try {
      if (
        this._iframe?.contentWindow?.location.href === 'about:blank' ||
        !this._iframe?.contentWindow?.location.href
      ) {
        if (this._hasNavigatedToAboutBlank) {
          this._onError(
            OnePlayerError.createGenericError(
              'fatal',
              'loadIframe',
              'iframeForciblyUnloaded',
              undefined, // originalError
              undefined // message
            )
          );
        }
        this._hasNavigatedToAboutBlank = true;
      }
    } catch (e) {
      // First load in Firefox throws a DOMException when trying to access the href
      if (e instanceof DOMException) {
        this._hasNavigatedToAboutBlank = true;
      }
      // Ignore exceptions - they are expected if the iframe has loaded successfully from another origin
    }
  }

  private _onTitleOrDisplayNameUpdate(value: string | undefined): void {
    this._iframe?.setAttribute('title', value ? value : this.defaultIframeTitle);
  }
}
